import axios from "axios";
import React, { useEffect, useState, useContext } from "react";
import { Container, Card, Badge, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faReceipt,
  faHashtag,
  faInfoCircle,
  faCalendarAlt,
  faClock,
  faStore,
  faCreditCard,
  faUser,
  faPhone,
} from "@fortawesome/fontawesome-free-solid";
import { getCookies } from "../../hooks/useCookies";
import { TableContext } from "../../contexts/tableContext";
import styles from "./viewOrderPopup.module.scss";
import envValues from "../../enviornment";
import Loader from "../../components/Loader/Loader";
import PropTypes from "prop-types";
import moment from "moment/moment";
import EntityProfileLink from "../../components/ProfileLinks/EntityProfileLink";
import BaristiCalculationsModal from "./BaristiCalculationsModal";
import IssueRefundModal from "./IssueRefundModal";
import getPaymentIcon from "./paymentMethodIcon";

const OrderInfoField = ({ icon, label, children }) => (
  <div className={styles.infoTile}>
    <div className={styles.infoIconWrap}>
      <FontAwesomeIcon icon={icon} aria-hidden="true" />
    </div>
    <div className={styles.infoContent}>
      <span className={styles.infoLabel}>{label}</span>
      <div className={styles.infoValue}>{children}</div>
    </div>
  </div>
);

OrderInfoField.propTypes = {
  icon: PropTypes.object.isRequired,
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

const CARD_REFUND_METHODS = ["card", "Apple Pay", "Google Pay"];
const CLOSED_ORDER_STATUSES = ["cancelled", "completed", "rejected"];

const getRefundButtonState = (order) => {
  const cardRefunds = Array.isArray(order?.cardRefunds) ? order.cardRefunds : [];
  const pending = cardRefunds.some((entry) => entry?.state === "pending");
  const latest = cardRefunds.length ? cardRefunds[cardRefunds.length - 1] : null;
  const isCardPayment = CARD_REFUND_METHODS.includes(order?.paymentMethod);
  const closed = CLOSED_ORDER_STATUSES.includes(order?.orderStatus);

  if (pending) {
    return { hidden: false, disabled: true, label: "Refund initiated" };
  }
  if (latest?.state === "failed" && isCardPayment) {
    return { hidden: false, disabled: false, label: "Issue Refund" };
  }
  if (order?.refundCompleted || closed) {
    return { hidden: true, disabled: true, label: "Issue Refund" };
  }
  return { hidden: false, disabled: false, label: "Issue Refund" };
};

const ViewOrderDetailsPopup = React.memo(({ orderId, handleClose }) => {
  const { formthemeStyle, bodyheader } = useContext(TableContext);
  const token = `Bearer ${getCookies("Token")}`;
  const [loading, setLoading] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [baristiData, setBaristiData] = useState(null);
  const [error, setError] = useState("");
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showFinanceModal, setShowFinanceModal] = useState(false);

  const loadOrderDetails = async () => {
    if (!orderId) return;

    setLoading(true);
    setError("");

    try {
      const response = await axios.get(
        `${envValues.REACT_APP_API_ENDPOINT}/orders/${orderId}`,
        {
          headers: { Authorization: token },
        }
      );

      if (response.status === 200) {
        setOrderDetails(response.data.data.result);
        setBaristiData(response.data.data.baristiCalculations);
      } else {
        setError("Failed to fetch order details");
      }
    } catch (err) {
      console.error("Error fetching order:", err);
      setError(err?.response?.data?.message || "Failed to fetch order details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    loadOrderDetails();
  }, [orderId, token]);

  const getStatusBadgeVariant = (status) => {
    const statusMap = {
      received: "primary",
      preparing: "info",
      ready: "warning",
      delivered: "success",
      completed: "success",
      cancelled: "danger",
      failed: "danger",
      rejected: "danger",
      pending: "secondary",
    };
    return statusMap[status?.toLowerCase()] || "secondary";
  };

  const formatCurrency = (amount) => {
    const n = Number(amount);
    if (Number.isNaN(n)) return "$0.00";
    return `$${n.toFixed(2)}`;
  };

  /** Shows negative amounts as -$X.XX (matches mobile “Processing charge”) */
  const formatCurrencySigned = (amount) => {
    const n = Number(amount);
    if (Number.isNaN(n) || n === 0) return "$0.00";
    const abs = Math.abs(n).toFixed(2);
    return n < 0 ? `-$${abs}` : `$${abs}`;
  };

  const getPaymentBreakdown = (order) => {
    if (!order) return null;
    const price = Number(order.itemAmount) || 0;
    const addonAmt = Number(order.addonAmount) || 0;
    const discount = Number(order.discountAmount) || 0;
    const couponAmt = Number(order.couponAmount) || 0;
    const subTotal = price + addonAmt - discount - couponAmt;
    const ccProc = Number(order.ccProcessingCharge) || 0;
    const tipCcProc = Number(order.tipCcProcessingCharge) || 0;
    const processingCombined = ccProc + tipCcProc;
    const tip = Number(order.tipAmount) || 0;
    const totalPayment =
      order.totalAmount != null
        ? Number(order.totalAmount)
        : Number(order.vendorAmountWithTip) || 0;
    return {
      price,
      addonAmt,
      discount,
      couponAmt,
      subTotal,
      processingCombined,
      tip,
      totalPayment,
    };
  };

  const refundButton = getRefundButtonState(orderDetails);

  return (
    <div className={styles.popupbox}>
      <div className={styles[formthemeStyle]} id={styles.box}>
        <div className={styles.popupHeader}>
          <h5 className={styles[bodyheader]} id={styles.addheading}>
            Order Details
          </h5>
          <span
            className={styles.closeicon}
            role="button"
            tabIndex={0}
            onClick={handleClose}
            onKeyPress={handleClose}
            aria-label="Close popup"
          >
            <FontAwesomeIcon icon={faTimes} />
          </span>
        </div>

        <div className={styles.popupBody}>
          {loading ? (
            <div className={styles.loaderContainer}>
              <Loader />
            </div>
          ) : error ? (
            <div className={styles.errorContainer}>
              <p className={styles.errorText}>{error}</p>
            </div>
          ) : orderDetails ? (
            <Container fluid className={styles.orderContainer}>
            {/* Order Info Card */}
            <Card className={`${styles.orderCard} ${styles.orderInfoCard}`}>
              <Card.Body>
                <div className={styles.orderInfoHeader}>
                  <div className={styles.orderInfoHeaderIconWrap}>
                    <FontAwesomeIcon icon={faReceipt} aria-hidden="true" />
                  </div>
                  <h6 className={styles.orderInfoTitle}>Order Information</h6>
                </div>

                <div className={styles.infoGrid}>
                  <OrderInfoField icon={faHashtag} label="Order ID">
                    #{orderDetails?.order_id}
                  </OrderInfoField>

                  <OrderInfoField icon={faInfoCircle} label="Status">
                    <Badge
                      bg={getStatusBadgeVariant(orderDetails?.orderStatus)}
                      className={styles.statusBadge}
                    >
                      {orderDetails?.orderStatus?.toUpperCase() || "N/A"}
                    </Badge>
                  </OrderInfoField>

                  <OrderInfoField icon={faCalendarAlt} label="Date">
                    {moment(orderDetails?.createdAt).format("MMM DD, YYYY")}
                  </OrderInfoField>

                  <OrderInfoField icon={faClock} label="Time">
                    {moment(orderDetails?.createdAt).format("hh:mm A")}
                  </OrderInfoField>

                  <OrderInfoField icon={faStore} label="Cafe">
                    <EntityProfileLink
                      type="cafe"
                      entity={orderDetails?.cafe}
                    />
                  </OrderInfoField>

                  <OrderInfoField
                    icon={
                      getPaymentIcon(orderDetails?.paymentMethod) || faCreditCard
                    }
                    label="Payment Method"
                  >
                    {orderDetails?.paymentMethod || "N/A"}
                  </OrderInfoField>

                  {orderDetails?.user && (
                    <>
                      <OrderInfoField icon={faUser} label="Customer Name">
                        <EntityProfileLink
                          type="user"
                          entity={orderDetails?.user}
                        />
                      </OrderInfoField>

                      <OrderInfoField icon={faPhone} label="Customer Phone">
                        <EntityProfileLink
                          type="user"
                          entity={orderDetails?.user}
                          label={orderDetails?.user?.phone || "N/A"}
                        />
                      </OrderInfoField>
                    </>
                  )}
                </div>
              </Card.Body>
            </Card>

            {/* Items Ordered */}
            <Card className={styles.orderCard}>
              <Card.Body>
                <h6 className={styles.sectionTitle}>Items Ordered</h6>
                {orderDetails?.items?.length > 0 ? (
                  orderDetails.items.map((item, index) => (
                    <div
                      key={index}
                      className={`${styles.orderItem} ${item?.isCanceled ? styles.canceledItem : ""}`}
                    >
                      <div className={styles.itemHeader}>
                        <span className={styles.drinkName}>
                          {item?.drink?.name || "Unknown Item"}
                        </span>
                        <span className={styles.quantity}>x{item?.quantity || 1}</span>
                      </div>
                      
                      {item?.variation && (
                        <div className={styles.itemDetail}>
                          <span className={styles.detailLabel}>Variation:</span>
                          <span className={styles.detailValue}>
                            {item?.drink?.variations?.find(
                              (opt) => opt.itemId === item.variation
                            )?.name || item?.variation}
                          </span>
                        </div>
                      )}

                      {item?.addOns?.length > 0 && (
                        <div className={styles.itemDetail}>
                          <span className={styles.detailLabel}>Add-Ons:</span>
                          <span className={styles.detailValue}>
                            {item.addOns
                              .flatMap((set) =>
                                set.selectedAddOns?.map((add) => add.addOnName) || []
                              )
                              .join(", ") || "None"}
                          </span>
                        </div>
                      )}

                      {item?.specialInstruction && (
                        <div className={styles.itemDetail}>
                          <span className={styles.detailLabel}>Instructions:</span>
                          <span className={styles.detailValue}>
                            {item.specialInstruction}
                          </span>
                        </div>
                      )}

                      {item?.isCanceled && (
                        <Badge bg="danger" className={styles.canceledBadge}>
                          Cancelled
                        </Badge>
                      )}
                    </div>
                  ))
                ) : (
                  <p className={styles.noItems}>No items found</p>
                )}
              </Card.Body>
            </Card>

            {/* Payment Summary (aligned with mobile app breakdown) */}
            <Card className={`${styles.orderCard} ${styles.paymentSummaryCard}`}>
              <Card.Body>
                <h6 className={styles.paymentSummaryHeading}>Payment Summary</h6>
                {(() => {
                  const p = getPaymentBreakdown(orderDetails);
                  if (!p) return null;
                  return (
                    <>
                      <div className={styles.paymentRow}>
                        <span>Price</span>
                        <span>{formatCurrency(p.price)}</span>
                      </div>
                      <div className={styles.paymentRow}>
                        <span>Add-Ons</span>
                        <span>{formatCurrency(p.addonAmt)}</span>
                      </div>
                      {p.discount > 0 && (
                        <div className={styles.paymentRow}>
                          <span>Discount</span>
                          <span>-{formatCurrency(p.discount)}</span>
                        </div>
                      )}
                      {p.couponAmt > 0 && (
                        <div className={styles.paymentRow}>
                          <span>Coupon</span>
                          <span>-{formatCurrency(p.couponAmt)}</span>
                        </div>
                      )}
                      <div className={`${styles.paymentRow} ${styles.paymentRowMuted}`}>
                        <span>Sub Total</span>
                        <span>{formatCurrency(p.subTotal)}</span>
                      </div>
                      <div className={styles.paymentRow}>
                        <span>Processing charge</span>
                        <span className={styles.processingValue}>
                          {p.processingCombined !== 0
                            ? formatCurrencySigned(-p.processingCombined)
                            : formatCurrency(0)}
                        </span>
                      </div>
                      <div className={styles.paymentRow}>
                        <span>Tip</span>
                        <span>{formatCurrency(p.tip)}</span>
                      </div>
                      {orderDetails?.refundAmount > 0 && (
                        <div className={`${styles.paymentRow} ${styles.refundRow}`}>
                          <span>Refund Amount</span>
                          <span>-{formatCurrency(orderDetails.refundAmount)}</span>
                        </div>
                      )}
                      <div className={styles.paymentTotalDivider} />
                      <div className={`${styles.paymentRow} ${styles.totalPaymentRow}`}>
                        <span>Total Payment</span>
                        <span>{formatCurrency(p.totalPayment)}</span>
                      </div>
                    </>
                  );
                })()}
              </Card.Body>
            </Card>

            {/* Cost Absorber Info (if refund exists) */}
            {orderDetails?.costAbsorber && orderDetails?.refundAmount > 0 && (
              <Card className={styles.orderCard}>
                <Card.Body>
                  <h6 className={styles.sectionTitle}>Refund Information</h6>
                  <div className={styles.paymentRow}>
                    <span>Cost Absorber</span>
                    <span>{orderDetails?.costAbsorber}</span>
                  </div>
                  {orderDetails?.cancelReason && (
                    <div className={styles.paymentRow}>
                      <span>Cancel Reason</span>
                      <span>{orderDetails?.cancelReason}</span>
                    </div>
                  )}
                </Card.Body>
              </Card>
            )}

            </Container>
          ) : (
            <div className={styles.noOrderContainer}>
              <p>No order details available</p>
            </div>
          )}
        </div>

        {orderDetails && !loading && !error && (
          <div className={styles.popupFooter}>
            <div className={styles.actionButtonsRow}>
              <Button
                variant="info"
                onClick={() => setShowFinanceModal(true)}
              >
                Baristi Calculations
              </Button>
              {!refundButton.hidden && (
                  <Button
                    variant="warning"
                    disabled={refundButton.disabled}
                    onClick={() => setShowRefundModal(true)}
                  >
                    {refundButton.label}
                  </Button>
                )}
            </div>
          </div>
        )}
      </div>

      <BaristiCalculationsModal
        show={showFinanceModal}
        onHide={() => setShowFinanceModal(false)}
        baristiData={baristiData}
        freeCoffeeAdded={orderDetails?.freeCoffeeAdded}
      />

      <IssueRefundModal
        show={showRefundModal}
        onHide={() => setShowRefundModal(false)}
        orderDetails={orderDetails}
        token={token}
        onSuccess={loadOrderDetails}
      />
    </div>
  );
});

ViewOrderDetailsPopup.propTypes = {
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  handleClose: PropTypes.func.isRequired,
};

export default ViewOrderDetailsPopup;

